import { FC, MouseEvent, useContext, useEffect, useMemo, useState } from 'react'

import { Icon, Option } from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { FileError } from 'react-dropzone'
import { useSelector } from 'react-redux'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'

import { isLiteRetailer as isLiteRetailerSelector } from 'store/currentUser/selectors'

import { ImportOrdersModalIds } from '../../orderImportModal.ids'
import { ImportOrdersContext } from '../ImportOrders.context'
import { ValidationResult } from '../ImportOrders.types'
import { ImportOrdersStandardContext } from '../ImportOrdersStandard.context'
import { ImportOrdersVerticalHorizontalContext } from '../ImportOrdersVerticalHorizontal.context'
import Dropzone from './DropZone/DropZone'
import FileSelectedBox from './FileSelectedBox/FileSelectedBox'
import {
  useAccountTemplateConstraints,
  useGetBrandStyleIdentifiers,
  useTemplateTypes,
} from './FirstStep.hooks'
import {
  DownloadLink,
  FileInfoContainer,
  StyledDropdown,
  TemplateSelectorContainer,
  UploadFileTile,
} from './FirstStep.styles'
import {
  SimplifiedTypes,
  dropErrorsMessageMap,
  getTemplateLink,
  getTemplateOptions,
  validateFile,
  validateStandardFiles,
} from './FirstStep.utils'

const FirstStep: FC = () => {
  const {
    selectedFiles,
    setSelectedFiles,
    setFileType,
    setTemplateId,
    isNextDisabled,
    setNextDisabled,
    errorMsg,
    setErrorMsg,
    templateType,
    setTemplateType,
    setIncludeDoor,
    setIncludedRetailer,
    setModalTitle,
    setShowLoader,
    oneFileNoDoor,
    setOneFileNoDoor,
  } = useContext(ImportOrdersContext)

  const { clearSecondStepVerticalHorizontalValues } = useContext(
    ImportOrdersVerticalHorizontalContext,
  )

  const { clearSecondStepStandardValues } = useContext(
    ImportOrdersStandardContext,
  )

  const [templateError, setTemplateError] = useState<string>('')
  const [dropErrors, setDropErrors] = useState<string[]>([])

  const isLiteRetailer: boolean = useSelector(isLiteRetailerSelector)
  const { useImportModal } = useFlags()

  const { styleIdentifiers, loadingStyleIdentifiers, styleIdentifiersError } =
    useGetBrandStyleIdentifiers(isLiteRetailer || useImportModal)

  const accountTemplateConstraints = useAccountTemplateConstraints(
    styleIdentifiers,
    isLiteRetailer,
    useImportModal,
  )

  const { templateConfigs, loadingTemplates, templatesError } =
    useTemplateTypes(accountTemplateConstraints)

  const { trackEvent } = useUserAnalytics()

  useEffect(() => setModalTitle(''), [setModalTitle])

  useEffect(() => {
    if (templateType && selectedFiles) {
      setNextDisabled(false)
      return
    }
    if (!isNextDisabled) setNextDisabled(true)
  }, [])

  useEffect(() => {
    if (styleIdentifiersError || templatesError) {
      setNextDisabled(true)
      return setTemplateError(
        'There was an error retrieving the flexible order template configurations',
      )
    }
    if (dropErrors?.length) {
      return setNextDisabled(true)
    }
    if (errorMsg !== '') setErrorMsg('')
    if (templateError !== '') setTemplateError('')
  }, [styleIdentifiersError, templatesError, dropErrors])

  const templateOptions = useMemo(
    () => getTemplateOptions(isLiteRetailer, templateConfigs),
    [isLiteRetailer, templateConfigs],
  )

  const onTemplateTypeSelectHandler: (args: {
    selected: Option | null
  }) => void = ({ selected }) => {
    if (selected === templateType) return
    if (errorMsg !== '') setErrorMsg('')
    if (templateError !== '') setTemplateError('')
    setTemplateType(selected)
    setSelectedFiles([])
    setTemplateId('')
    setNextDisabled(true)
    clearSecondStepVerticalHorizontalValues()
    clearSecondStepStandardValues()
  }

  const onFileSelectedHandler = async (
    files: File[],
    errors?: FileError[],
  ): Promise<void> => {
    if (!templateType) {
      setTemplateError('Please, select template type first')
      return
    }
    if (errors?.length) {
      setDropErrors(
        errors?.map(
          (err) =>
            dropErrorsMessageMap?.[err.code] ??
            'There was an error while uploading the file.',
        ),
      )
      return
    }

    trackEvent('Orders List_Import Orders Modal File Added')
    setShowLoader(true)

    const validationResult: ValidationResult =
      templateType?.value === SimplifiedTypes.Standard
        ? await validateStandardFiles(files, selectedFiles)
        : await validateFile(
            files[0],
            templateType.value as SimplifiedTypes,
            templateConfigs,
          )

    setShowLoader(false)
    if (!validationResult?.isValid) {
      setNextDisabled(selectedFiles.length ? false : true)
      setTemplateError(validationResult?.errorMsg!)
      return
    }

    if (errorMsg !== '') setErrorMsg('')
    if (templateError !== '') setTemplateError('')
    setSelectedFiles(selectedFiles ? selectedFiles.concat(files) : files)
    setTemplateId(validationResult?.templateId)
    setIncludeDoor(!!validationResult?.includeDoor)

    const shouldSetNoDoor = validationResult?.oneFileNoDoor || oneFileNoDoor
    if (shouldSetNoDoor) {
      setOneFileNoDoor(true)
      setIncludeDoor(false)
    }
    setIncludedRetailer(validationResult?.retailerName || '')
    setNextDisabled(false)
    setDropErrors([])
    setFileType(validationResult.type || '')
    clearSecondStepVerticalHorizontalValues()
    clearSecondStepStandardValues()
  }

  const changeToSecondTab = (): void => {
    trackEvent('Orders List_Import Orders Modal Click Download Example File')
    const buttons = document.getElementsByTagName('Button')
    for (let i = 0, length = buttons.length; i < length; i++) {
      if (buttons[i].innerHTML === 'Create Excel Orders Template') {
        ;(buttons[i] as HTMLElement).click()
        return
      }
    }
  }

  const onlyVerticalUPC =
    Array.isArray(templateConfigs.vertical) &&
    templateConfigs.vertical.length === 1 &&
    templateConfigs.vertical[0].constraint === 'upc'

  const showDownloadExampleFile =
    templateType &&
    ((templateType?.value === SimplifiedTypes.Standard && !isLiteRetailer) ||
      templateType?.value === SimplifiedTypes.Vertical ||
      templateType?.value === SimplifiedTypes.Horizontal)

  const exampleFileLinkProps =
    templateType?.value === SimplifiedTypes.Standard
      ? { onClick: changeToSecondTab }
      : {
          href: getTemplateLink(
            templateType?.value || '',
            isLiteRetailer,
            onlyVerticalUPC,
          ),
        }

  const onDeleteHandler = (e: MouseEvent, title: string): void => {
    const files: File[] = selectedFiles.filter((file) => file.name !== title)
    setSelectedFiles(files)
    setTemplateError('')
    if (files.length) setNextDisabled(false)
    else if (!isNextDisabled) setNextDisabled(true)
  }

  return (
    <>
      <TemplateSelectorContainer>
        <StyledDropdown
          id={ImportOrdersModalIds.SelectImportTemplateDropdown}
          label="Select an Import Template"
          labelId={
            onlyVerticalUPC
              ? ImportOrdersModalIds.SelectImportTemplateUPCLabelId
              : ImportOrdersModalIds.SelectImportTemplateLabelId
          }
          isRequired
          isLoading={loadingStyleIdentifiers || loadingTemplates}
          isDisabled={loadingStyleIdentifiers || loadingTemplates}
          placeholder="Select One"
          value={templateType}
          onSelect={onTemplateTypeSelectHandler}
          options={Object.values(templateOptions)}
          menuPosition="fixed"
        />
        {showDownloadExampleFile && (
          <DownloadLink {...exampleFileLinkProps}>
            Download example file with instructions
          </DownloadLink>
        )}
      </TemplateSelectorContainer>
      <UploadFileTile>Upload File</UploadFileTile>
      {selectedFiles &&
        selectedFiles.map((selectedFile) => (
          <FileSelectedBox
            title={selectedFile.name}
            onDelete={(e, title) => onDeleteHandler(e, title)}
            multipleFiles={templateType?.value === SimplifiedTypes.Standard}
          />
        ))}
      {((templateType?.value === SimplifiedTypes.Standard &&
        selectedFiles.length < 10) ||
        !selectedFiles.length) && (
        <Dropzone onFileSelected={onFileSelectedHandler} />
      )}
      {!isNextDisabled && !(dropErrors?.length > 0) && (
        <FileInfoContainer className="success">
          <Icon
            iconName="check-circle-on"
            iconSize="small"
            iconColor="success-500"
          />
          Uploaded files matches template
        </FileInfoContainer>
      )}
      {dropErrors?.map((err) => (
        <FileInfoContainer className="error">
          <Icon
            iconName="warning"
            iconSize="small"
            iconColor="error-500"
            plain
          />
          {err}
        </FileInfoContainer>
      ))}
      {templateError !== '' && (
        <FileInfoContainer className="error">
          <Icon iconName="warning" iconSize="small" iconColor="error-500" />
          {templateError}
        </FileInfoContainer>
      )}
    </>
  )
}

export default FirstStep
